import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CarsAvailabilitySelectors, CarSortOptionEnum } from "@apac/cars";
import {
  CarsAvailabilityActions,
  useFilterDialog as useCoreFilterDialog,
} from "@b2bportal/core-cars";
import { usePackageCarsAvailabilityFilters } from "../../hooks";

/**
 * Package-specific filter dialog hook that uses incremental pricing
 */
export const usePackageFilterDialog = () => {
  const { context, handlers } = useCoreFilterDialog();
  const dispatch = useDispatch();

  // Use package-specific filters hook to get correct filterOptions and handlers
  const {
    context: { totalFilterCount, filterOptions: packageFilterOptions },
    handlers: packageHandlers,
  } = usePackageCarsAvailabilityFilters();

  const selectedSort = useSelector(CarsAvailabilitySelectors.getSort);

  const [sortOption, setSortOption] = useState(selectedSort);

  const filtersChanged = useMemo(
    () => context.filtersChanged || sortOption !== selectedSort,
    [context.filtersChanged, sortOption, selectedSort]
  );

  const updateValuesToMatchState = useCallback(() => {
    handlers.updateValuesToMatchState();
    setSortOption(selectedSort);
  }, [handlers, selectedSort]);

  const resetAllFilters = useCallback(() => {
    // Reset all filters to default
    packageHandlers.resetFilters();
    // Then specifically reset price filter to actual min/max (not MAX_SAFE_INTEGER)
    packageHandlers.resetPriceFilter();
    // Reset sort to recommended
    setSortOption(CarSortOptionEnum.Recommended);
    dispatch(CarsAvailabilityActions.setSort(CarSortOptionEnum.Recommended));
  }, [dispatch, packageHandlers]);

  const applyFilter = useCallback(() => {
    handlers.applyFilter();
    dispatch(CarsAvailabilityActions.setSort(sortOption));
  }, [dispatch, handlers, sortOption]);

  return {
    context: {
      ...context,
      // Override filterOptions with package-specific ones (incremental pricing)
      filterOptions: packageFilterOptions,
      totalFilterCount,
      filtersChanged,
      sortOption,
    },
    handlers: {
      ...handlers,
      updateValuesToMatchState,
      resetAllFilters,
      applyFilter,
      onSortChange: setSortOption,
    },
  };
};
